import * as RS from '@radix-ui/react-switch'
import { useId, type ComponentProps, type ReactNode } from 'react'
import { cn } from './cn'

export interface SwitchProps extends ComponentProps<typeof RS.Root> {
  label?: ReactNode
  description?: ReactNode
  /** Put the label on the left and the switch on the right (settings rows). */
  labelPosition?: 'left' | 'right'
}

/** On and off toggle, 36 x 20, primary when on. */
export function Switch({ label, description, labelPosition = 'right', className, id, ...rest }: SwitchProps) {
  const autoId = useId()
  const switchId = id ?? `sw-${autoId}`
  const control = (
    <RS.Root
      id={switchId}
      className={cn(
        'peer relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-0 bg-line-strong p-0.5',
        // Invisible 44x40 hit area on phones (UI.md 10 tap targets) without changing the 36x20 look.
        'max-sm:after:absolute max-sm:after:-inset-x-1 max-sm:after:-inset-y-2.5 max-sm:after:content-[""]',
        'transition-colors duration-150 ease-out focus-ring data-[state=checked]:bg-primary',
        'disabled:cursor-not-allowed disabled:opacity-50',
        !label && className,
      )}
      {...rest}
    >
      <RS.Thumb
        className={cn(
          'block size-4 rounded-full bg-white shadow-[0_1px_2px_rgb(15_23_42/0.25)] transition-transform duration-150 ease-out',
          'data-[state=checked]:translate-x-4 data-[state=checked]:bg-on-primary',
        )}
      />
    </RS.Root>
  )
  if (!label) return control
  return (
    <div className={cn('flex items-start gap-3', labelPosition === 'left' && 'flex-row-reverse justify-between', className)}>
      <div className="flex h-[21px] items-center">{control}</div>
      <label htmlFor={switchId} className="min-w-0 cursor-pointer text-body text-text select-none">
        {label}
        {description && <span className="mt-0.5 block text-meta text-muted">{description}</span>}
      </label>
    </div>
  )
}
